import {useCallback, useMemo} from 'preact/hooks';

import {Modal} from '~/components/Modal';
import {ShopAppIcon} from '~/components/ShopAppIcon';
import {useI18n} from '~/foundation/I18n/hooks';

import {useInstallments} from '../context/InstallmentsContext';
import {usePaymentTermsMonorail} from '../monorail';

import {PrequalModalContent} from './PrequalModalContent';
import {SamplePlansModalContent} from './SamplePlansModalContent';
import {SplitPayModalContent} from './SplitPayModalContent';

export const InstallmentsModal = () => {
  const {
    modalToken,
    modalVariant,
    isModalOpen,
    setIsModalOpen,
    countryCode,
    loanTypes,
  } = useInstallments();
  const {translate} = useI18n();
  const {trackModalAction} = usePaymentTermsMonorail();

  const handleOnDismiss = useCallback(() => {
    trackModalAction({
      modalToken,
      action: 'close',
    });
    setIsModalOpen(false);
  }, [modalToken, setIsModalOpen, trackModalAction]);

  const content = useMemo(() => {
    switch (modalVariant) {
      case 'prequal':
        return <PrequalModalContent onClose={handleOnDismiss} />;
      case 'samplePlans':
        return <SamplePlansModalContent />;
      case 'splitPay':
      default:
        return <SplitPayModalContent />;
    }
  }, [handleOnDismiss, modalVariant]);

  const headerTitleGB = useMemo(() => {
    // Only Split Pay
    if (loanTypes.length === 1 && loanTypes[0] === 'split_pay') {
      return translate('paymentTerms.installmentsModal.titleGB');
    }
    // Split Pay and Interest
    else if (
      loanTypes.includes('interest') &&
      loanTypes.includes('split_pay')
    ) {
      return translate('paymentTerms.installmentsModal.titleGB');
    }
    // Any option containing zero_percent
    else if (loanTypes.includes('zero_percent')) {
      return translate('paymentTerms.installmentsModal.title');
    }
    // Only interest
    else {
      return translate('paymentTerms.installmentsModal.titleInformational');
    }
  }, [loanTypes, translate]);

  const headerTitle = useMemo(() => {
    switch (countryCode) {
      case 'GB':
        return headerTitleGB;
      default:
        return translate('paymentTerms.installmentsModal.title');
    }
  }, [countryCode, headerTitleGB, translate]);

  return (
    <Modal
      variant="default"
      visible={isModalOpen}
      onDismiss={handleOnDismiss}
      headerLogo={<ShopAppIcon className="size-8 text-purple-primary" />}
      headerTitle={headerTitle as string}
    >
      <section
        data-testid="shopify-payment-terms-modal"
        className="w-[432px] max-w-full p-8 pt-0 font-system text-grayscale-d2/70"
      >
        <div hidden id="shopify-payment-terms-modal-warning-text">
          {translate('paymentTerms.installmentsModal.newWindow')}
        </div>
        {content}
      </section>
    </Modal>
  );
};
